import { Component, h, Prop, State, Method, Element } from '@stencil/core';
import ChatSDK from 'test-iso-chat-sdk';
import '../../../stories/Components/Loader/Spinner/Spinner';
import { showToast } from '../../../stories/Components/Toaster/toast-helper';
import '../../../stories/Components/ProfileTab/ProfileTab';

@Component({
  tag: 'profile-page',
  styleUrl: 'profile-page.css',
  shadow: true,
})
export class ProfilePage {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) name: string = '';
  @Prop({ mutable: true }) profilePhotoUrl: string = '';

  @State() isEditingName: boolean = false;
  @State() newName: string = '';
  @State() isLoading: boolean = false;
  @State() selectedFile: File | null = null;
  @State() tempProfilePhotoUrl: string = '';
  @Prop() conversationId: string = '';
  @Prop() getUserDetails: () => void;
  @Prop() closeDrawer: () => void;
  @Prop() toggleTheme: () => void;
  @Prop() defaultTheme: string;

  // Lifecycle methods
  componentWillLoad() {
    this.newName = this.name;
    this.tempProfilePhotoUrl = this.profilePhotoUrl;
  }

  // API methods
  @Method()
  async getPresignedUrl(file: File): Promise<string> {
    try {
      const mediaExtension = file.name.split('.').pop();
      const res = await ChatSDK.getInstance().user.getPresignedurlUpdate({
        mediaExtension,
      });

      // Upload file to presigned URL
      await fetch(res.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Return the final URL for the uploaded file
      return res.mediaUrl;
    } catch (error) {
      console.error('Error getting presigned URL or uploading file:', error);
      throw error;
    }
  }

  @Method()
  async updateUserProfile(payload: { name?: string; profilePhotoUrl?: string }): Promise<void> {
    this.isLoading = true;
    try {
      await ChatSDK.getInstance().user.patchUser({
        userName: payload.name,
        userProfileImageUrl: payload.profilePhotoUrl,
      });

      // Update local properties immediately for real-time update
      if (payload.name) {
        this.name = payload.name;
      }
      if (payload.profilePhotoUrl) {
        this.profilePhotoUrl = payload.profilePhotoUrl;
      }

      this.isLoading = false;
      showToast({
        message: 'Profile Updated Successfully!',
        type: 'success',
        position: 'bottom-right',
      });
    } catch (error) {
      this.isLoading = false;
      showToast({
        message: 'Failed to update profile',
        type: 'error',
        position: 'bottom-right',
      });
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  private validateNameInput = (input: string): string => {
    return input?.toLowerCase()?.replace(/[^a-z0-9_]/g, '').slice(0, 20);
  };
  

  private handleNameEdit = () => {
    this.isEditingName = true;
        requestAnimationFrame(() => {
      const profileComponent = this.el.shadowRoot?.querySelector('profile-tab');
      const inputElement = profileComponent?.shadowRoot?.querySelector('.name-input') as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
        const length = inputElement.value.length;
        inputElement.setSelectionRange(length, length);
      }
    });
  };

  private handleNameChange = (event: Event) => {
    const inputElement = event.target as HTMLInputElement;
    const originalValue = inputElement.value;
    const cursorPosition = inputElement.selectionStart || 0;
    
    this.newName = this.validateNameInput(originalValue);
    
    if (this.newName !== originalValue) {
      const beforeCursor = originalValue.substring(0, cursorPosition);
      const validatedBeforeCursor = this.validateNameInput(beforeCursor);
      const diff = beforeCursor.length - validatedBeforeCursor.length;
      
      inputElement.value = this.newName;
      const newPosition = Math.max(0, cursorPosition - diff);
      
      setTimeout(() => {
        inputElement.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  private handleNameSave = async () => {
    try {
      if (this.newName !== this.name) {
        await this.updateUserProfile({ name: this.newName });
        // getUserDetails is still called to sync with server
        await this.getUserDetails();
      }
      this.isEditingName = false;
    } catch (error) {
      console.error('Error saving name:', error);
    }
  };

  private handleNameKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.handleNameSave();
    } else if (event.key === 'Escape') {
      this.newName = this.name;
      this.isEditingName = false;
    }
  };

  private handleFileChange = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    try {
      this.selectedFile = file;

      // Create a temporary URL for preview
      const tempUrl = URL.createObjectURL(file);
      this.tempProfilePhotoUrl = tempUrl;

      input.value = '';
    } catch (error) {
      console.error('Error handling file selection:', error);
      input.value = '';
    }
  };

  private handleManualUpdate = async () => {
    if (!this.selectedFile && this.newName === this.name) {
      showToast({
        message: 'No changes to update',
        type: 'info',
        position: 'bottom-right',
      });
      return;
    }

    try {
      this.isLoading = true;
      let mediaUrl = this.profilePhotoUrl;

      if (this.selectedFile) {
        mediaUrl = await this.getPresignedUrl(this.selectedFile);
      }

      await this.updateUserProfile({
        name: this.newName !== this.name ? this.newName : undefined,
        profilePhotoUrl: this.selectedFile ? mediaUrl : undefined,
      });

      // Clean up after successful update
      if (this.selectedFile) {
        // Revoke the temporary object URL to prevent memory leaks
        if (this.tempProfilePhotoUrl && this.tempProfilePhotoUrl.startsWith('blob:')) {
          URL.revokeObjectURL(this.tempProfilePhotoUrl);
        }
        // Update the tempProfilePhotoUrl to match the new profilePhotoUrl
        this.tempProfilePhotoUrl = this.profilePhotoUrl;
        this.selectedFile = null;
      }

      // Still call getUserDetails to ensure server-side sync
      await this.getUserDetails();
    } catch (error) {
      console.error('Error updating profile:', error);
      this.isLoading = false;
    }
  };

  private cancelChanges = () => {
    this.newName = this.name;
    this.isEditingName = false;

    if (this.selectedFile) {
      this.selectedFile = null;

      // Only revoke if it's a blob URL
      if (this.tempProfilePhotoUrl && this.tempProfilePhotoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.tempProfilePhotoUrl);
      }
      this.tempProfilePhotoUrl = this.profilePhotoUrl;
    }
  };

  render() {
    const hasChanges = this.newName !== this.name || this.selectedFile !== null;

    return (
      <profile-tab
        name={this.name}
        isLoading={this.isLoading}
        tempProfilePhotoUrl={this.tempProfilePhotoUrl}
        isEditingName={this.isEditingName}
        newName={this.newName}
        hasChanges={hasChanges}
        handleNameEdit={this.handleNameEdit}
        handleNameChange={this.handleNameChange}
        handleNameKeyDown={this.handleNameKeyDown}
        handleNameSave={this.handleNameSave}
        handleFileChange={this.handleFileChange}
        handleManualUpdate={this.handleManualUpdate}
        cancelChanges={this.cancelChanges}
        closeDrawer={this.closeDrawer}
        defaultTheme={this.defaultTheme}
        toggleTheme={this.toggleTheme}
      />
    );
  }
}
